import React, { useEffect, useState } from "react";
import { Group, Modal, Select, Stack, Text, TextInput } from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { OrganisationLevel, OrganisationNode, OrganisationType } from "@models/access";
import { accessApi } from "@services/accessApi";
import { errorMessage } from "@hooks/monitoring/useApiResource";

interface CreateOrganisationModalProps {
  opened: boolean;
  /** The root organisation (DSML Internal): every new organisation is its direct child. */
  rootName: string;
  onClose: () => void;
  onCreated: (organisation: OrganisationNode) => void;
}

const TYPES: OrganisationType[] = ["INTERNAL", "EXTERNAL"];
const LEVELS: OrganisationLevel[] = ["LEGAL_ENTITY", "LOCATION"];

/**
 * Create an organisation directly under the root. Core refuses any other parent
 * and copies the root's address, so only the name, type and level are asked.
 */
const CreateOrganisationModal: React.FC<CreateOrganisationModalProps> = ({ opened, rootName, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [type, setType] = useState<OrganisationType>("INTERNAL");
  const [level, setLevel] = useState<OrganisationLevel>("LEGAL_ENTITY");
  const [externalId, setExternalId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setName("");
    setType("INTERNAL");
    setLevel("LEGAL_ENTITY");
    setExternalId("");
    setError(null);
  }, [opened]);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const created = await accessApi().createOrganisation({
        name: name.trim(), organisationType: type, organisationLevel: level,
        externalId: externalId.trim() || undefined,
      });
      onCreated(created);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t("admin.access.organisations.create.title")} size="md"
           portalProps={{ target: "#admin-portal-root" }}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">{t("admin.access.organisations.create.hint", { root: rootName })}</Text>
        <TextInput label={t("admin.access.organisations.create.name")} value={name} required data-autofocus
                   onChange={(e) => setName(e.currentTarget.value)}
                   onKeyDown={(e) => e.key === "Enter" && void submit()} />
        <Group grow gap="md">
          <Select
            label={t("admin.access.organisations.create.type")}
            data={TYPES.map((v) => ({ value: v, label: t(`admin.access.organisations.type.${v}`) }))}
            value={type}
            onChange={(v) => setType((v as OrganisationType) ?? "INTERNAL")}
            allowDeselect={false}
            comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
          />
          <Select
            label={t("admin.access.organisations.create.level")}
            data={LEVELS.map((v) => ({ value: v, label: t(`admin.access.organisations.level.${v}`) }))}
            value={level}
            onChange={(v) => setLevel((v as OrganisationLevel) ?? "LEGAL_ENTITY")}
            allowDeselect={false}
            comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
          />
        </Group>
        <TextInput label={t("admin.access.organisations.create.externalId")}
                   description={t("admin.access.organisations.create.externalIdHint")}
                   value={externalId} onChange={(e) => setExternalId(e.currentTarget.value)} />
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={submit} loading={saving} disabled={!name.trim()}>
            {t("admin.access.organisations.create.submit")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CreateOrganisationModal;
